import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = useAuthStore.getState().refreshToken;

      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          useAuthStore.getState().setTokens(accessToken, newRefreshToken);

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          return api(originalRequest);
        } catch {
          // Refresh failed, logout user
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }
      } else {
        // No refresh token, logout
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API errors
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error || error.message || 'An error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
}

// API endpoints
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    company?: string;
  }) => api.post('/auth/register', data),

  logout: (refreshToken?: string) =>
    api.post('/auth/logout', { refreshToken }),

  refresh: (refreshToken: string) =>
    api.post('/auth/refresh', { refreshToken }),

  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
};

export const userApi = {
  getMe: () => api.get('/users/me'),

  updateMe: (data: { firstName?: string; lastName?: string; phone?: string; company?: string }) =>
    api.patch('/users/me', data),

  list: (params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
    search?: string;
  }) => api.get('/users', { params }),

  getById: (id: string) => api.get(`/users/${id}`),

  create: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
    role: string;
    company?: string;
    status?: string;
  }) => api.post('/users', data),

  update: (id: string, data: Partial<{
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    company: string;
    status: string;
  }>) => api.patch(`/users/${id}`, data),

  delete: (id: string) => api.delete(`/users/${id}`),
};

export const paymentLinkApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentType?: string;
    propertyLocation?: string;
    customerEmail?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/payment-links', { params }),

  getById: (id: string) => api.get(`/payment-links/${id}`),

  getByLinkCode: (linkCode: string) => api.get(`/payment-links/public/${linkCode}`),

  create: (data: {
    title: string;
    description?: string;
    paymentType: string;
    amount?: number;
    currency?: string;
    allowPartialPayment?: boolean;
    minimumAmount?: number;
    expiresAt?: string;
    customerEmail?: string;
    customerName?: string;
    customerPhone?: string;
    propertyLocation?: string;
    unitNumber?: string;
    contractNumber?: string;
    sendEmailOnCreate?: boolean;
    sendReminderEmails?: boolean;
    // Line items support
    lineItems?: Array<{
      description: string;
      paymentType?: string;
      quantity?: number;
      unitPrice: number;
      referenceNumber?: string;
      referenceDate?: string;
    }>;
    // Recurring payment support
    isRecurring?: boolean;
    recurringFrequency?: string;
    recurringStartDate?: string;
    recurringEndDate?: string;
    recurringCount?: number;
  }) => api.post('/payment-links', data),

  update: (id: string, data: Partial<{
    title: string;
    description: string;
    paymentType: string;
    allowPartialPayment: boolean;
    minimumAmount: number;
    expiresAt: string;
    customerEmail: string;
    customerName: string;
    customerPhone: string;
    propertyLocation: string;
    unitNumber: string;
    contractNumber: string;
    sendReminderEmails: boolean;
  }>) => api.patch(`/payment-links/${id}`, data),

  cancel: (id: string) => api.post(`/payment-links/${id}/cancel`),

  getStatistics: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/payment-links/statistics', { params }),
};

export const transactionApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentMethod?: string;
    paymentLinkId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    minAmount?: number;
    maxAmount?: number;
  }) => api.get('/transactions', { params }),

  getById: (id: string) => api.get(`/transactions/${id}`),

  getStatistics: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/transactions/statistics', { params }),

  exportCsv: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/transactions/export/csv', {
      params,
      responseType: 'blob',
    }),
};

export const paymentApi = {
  getConfig: () => api.get('/payments/config'),

  getMethods: (linkCode: string) => api.get(`/payments/methods/${linkCode}`),

  createSession: (linkCode: string, amount?: number) =>
    api.post(`/payments/session/${linkCode}`, { amount }),

  submitPayment: (
    linkCode: string,
    paymentMethod: Record<string, unknown>,
    browserInfo?: Record<string, unknown>,
    amount?: number
  ) =>
    api.post(`/payments/submit/${linkCode}`, {
      paymentMethod,
      browserInfo,
      amount,
    }),

  submitDetails: (details: Record<string, unknown>, paymentData?: string) =>
    api.post('/payments/details', { details, paymentData }),

  confirmPayment: (linkCode: string, resultCode: string, sessionId?: string) =>
    api.post(`/payments/confirm/${linkCode}`, { resultCode, sessionId }),
};

export const templateApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    paymentType?: string;
    isActive?: boolean;
  }) => api.get('/templates', { params }),

  getById: (id: string) => api.get(`/templates/${id}`),

  create: (data: {
    name: string;
    description?: string;
    title: string;
    linkDescription?: string;
    paymentType: string;
    amount?: number;
    currency?: string;
    allowPartialPayment?: boolean;
    minimumAmount?: number;
    isRecurring?: boolean;
    recurringFrequency?: string;
    recurringCount?: number;
    propertyLocation?: string;
    sendEmailOnCreate?: boolean;
    sendReminderEmails?: boolean;
    lineItems?: Array<{
      description: string;
      paymentType?: string;
      quantity?: number;
      unitPrice: number;
    }>;
  }) => api.post('/templates', data),

  update: (id: string, data: Partial<{
    name: string;
    description: string;
    title: string;
    linkDescription: string;
    paymentType: string;
    amount: number;
    currency: string;
    allowPartialPayment: boolean;
    minimumAmount: number;
    isRecurring: boolean;
    recurringFrequency: string;
    recurringCount: number;
    propertyLocation: string;
    sendEmailOnCreate: boolean;
    sendReminderEmails: boolean;
    isActive: boolean;
    lineItems: Array<{
      description: string;
      paymentType?: string;
      quantity?: number;
      unitPrice: number;
    }>;
  }>) => api.patch(`/templates/${id}`, data),

  delete: (id: string) => api.delete(`/templates/${id}`),

  toggleActive: (id: string) => api.post(`/templates/${id}/toggle-active`),
};

export default api;
